import {
  AD_FORMAT_LABELS,
  AD_FORMATS,
  BID_STRATEGIES,
  BID_STRATEGY_LABELS,
  BILLING_EVENTS,
  CALL_TO_ACTION_LABELS,
  CALL_TO_ACTIONS,
  CAMPAIGN_OBJECTIVES,
  CAROUSEL_MAX_CARDS,
  DESTINATION_TYPE_LABELS,
  defaultBillingEvent,
  destinationOption,
  linkWithUtm,
  OBJECTIVE_DESTINATIONS,
  objectiveLabel,
  optimizationGoalLabel,
  PIXEL_EVENT_LABELS,
  PIXEL_EVENTS,
  PREVIEW_FORMATS,
  SPECIAL_AD_CATEGORIES,
  SPECIAL_AD_CATEGORY_LABELS,
  targetingFromMeta,
  validateDraftForPublish,
  WIZARD_STEP_LABELS,
  WIZARD_STEPS,
  type AdDraft,
  type AdSetDraft,
  type CreationOptionsDto,
  type CreativeAssetDto,
  type DraftDto,
  type PreviewDto,
  type WizardDraft,
  type WizardStep,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Eye,
  ImageIcon,
  Plus,
  Rocket,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { VariationsDialog } from "@/components/ai/variations-dialog";
import { AssetPicker, AssetThumb } from "@/components/creation/asset-picker";
import { PublicationProgress } from "@/components/creation/publication-progress";
import { PlacementsEditor, TargetingEditor } from "@/components/creation/targeting-editor";
import { ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { creationApi, creationKey } from "@/lib/creation-api";
import { queries } from "@/lib/queries";
import { cn, formatMoney, formatRelative } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/assistente_/$draftId")({
  component: WizardPage,
});

const NONE = "__none__";

function newAdSet(n: number): AdSetDraft {
  return {
    key: `a${Date.now()}${n}`,
    name: `Conjunto ${n}`,
    destinationType: "WEBSITE",
    optimizationGoal: "LINK_CLICKS",
    billingEvent: "LINK_CLICKS",
    pixelId: null,
    customEventType: null,
    pageId: null,
    applicationId: null,
    objectStoreUrl: null,
    budgetKind: "diario",
    budgetCents: 0,
    bidAmountCents: null,
    startTime: null,
    endTime: null,
    targeting: {
      countries: ["BR"],
      regions: [],
      cities: [],
      ageMin: 18,
      ageMax: 65,
      genders: [],
      interests: [],
      customAudiences: [],
      excludedCustomAudiences: [],
      advantageAudience: false,
    },
    placements: {
      automatic: true,
      publisherPlatforms: [],
      facebookPositions: [],
      instagramPositions: [],
      messengerPositions: [],
      audienceNetworkPositions: [],
    },
    ads: [],
  };
}

function newAd(n: number, pageId: string | null): AdDraft {
  return {
    key: `ad${Date.now()}${n}`,
    name: `Anúncio ${n}`,
    pageId,
    instagramUserId: null,
    format: "imagem",
    primaryText: "",
    headline: "",
    description: "",
    cta: "LEARN_MORE",
    link: "",
    utmEnabled: true,
    utm: {
      source: "facebook",
      medium: "paid",
      campaign: "{{campaign.name}}",
      content: "{{ad.name}}",
      term: "{{adset.name}}",
    },
    imageAssetId: null,
    videoAssetId: null,
    thumbnailAssetId: null,
    cards: [],
  };
}

function centsToInput(cents: number): string {
  return cents > 0 ? (cents / 100).toFixed(2).replace(".", ",") : "";
}
function inputToCents(value: string): number {
  const n = Number(value.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

function WizardPage() {
  const { workspaceId, draftId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const draftQuery = useQuery(queries.draft(workspaceId, draftId));
  if (draftQuery.isPending) return <LoadingState rows={6} />;
  if (draftQuery.isError)
    return <ErrorState error={draftQuery.error} onRetry={() => void draftQuery.refetch()} />;
  return (
    <Wizard
      key={draftQuery.data.id}
      workspaceId={workspaceId}
      initial={draftQuery.data}
      onDeleted={() => {
        void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
        void navigate({ to: "/app/$workspaceId/assistente", params: { workspaceId } });
      }}
    />
  );
}

function Wizard({
  workspaceId,
  initial,
  onDeleted,
}: {
  workspaceId: string;
  initial: DraftDto;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const account = initial.adAccountMetaId;
  const options = useQuery(queries.creationOptions(workspaceId, account));
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const currency = accounts.data?.find((a) => a.metaId === account)?.currency ?? "BRL";
  const [data, setData] = useState<WizardDraft>(initial.data);
  const [step, setStep] = useState<WizardStep>(initial.step);
  const [version, setVersion] = useState(initial.version);
  const [status, setStatus] = useState(initial.status);
  const [publicationId, setPublicationId] = useState<string | null>(initial.lastPublicationId);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string>(initial.updatedAt);
  const [activate, setActivate] = useState(false);
  const [adsetIndex, setAdsetIndex] = useState(0);
  const [highlight, setHighlight] = useState<string | null>(null);
  const issues = useMemo(() => validateDraftForPublish(data, currency), [data, currency]);
  const issueAt = (path: string) => issues.find((i) => i.path === path)?.message;

  const update = (patch: Partial<WizardDraft> | ((d: WizardDraft) => WizardDraft)) => {
    setData((d) => (typeof patch === "function" ? patch(d) : { ...d, ...patch }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: (input: { step: WizardStep; data: WizardDraft }) =>
      creationApi.saveDraft(workspaceId, initial.id, {
        adAccountMetaId: account,
        name: input.data.campaign.name,
        kind: initial.kind,
        step: input.step,
        data: input.data,
        version,
      }),
    onSuccess: (d) => {
      setVersion(d.version);
      setStatus(d.status);
      setSavedAt(d.updatedAt);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);
  // Salva sozinho 1,5 s depois da última mudança (e sempre ao trocar de etapa).
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => saveRef.current.mutate({ step, data }), 1500);
    return () => clearTimeout(t);
  }, [data, step, dirty]);

  const goTo = (next: WizardStep) => {
    setStep(next);
    save.mutate({ step: next, data });
    window.scrollTo({ top: 0 });
  };
  const stepIndex = WIZARD_STEPS.indexOf(step);

  const publish = useMutation({
    mutationFn: async () => {
      if (dirty || save.isPending)
        await creationApi.saveDraft(workspaceId, initial.id, {
          adAccountMetaId: account,
          name: data.campaign.name,
          kind: initial.kind,
          step: "revisao",
          data,
          version: null,
        });
      return creationApi.publish(workspaceId, initial.id, activate);
    },
    onSuccess: (p) => {
      setPublicationId(p.id);
      setStatus(
        p.status === "concluida" ? "publicado" : p.status === "executando" ? "publicando" : "erro",
      );
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "campaigns"] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => creationApi.deleteDraft(workspaceId, initial.id),
    onSuccess: onDeleted,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const saveTemplate = useMutation({
    mutationFn: () =>
      creationApi.createTemplate(workspaceId, {
        name: data.campaign.name || "Template",
        description: "",
        draftId: initial.id,
      }),
    onSuccess: (t) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      toast.success(`Template "${t.name}" salvo (sem ids da conta).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const jumpToIssue = (path: string) => {
    const m = /^adsets\.(\d+)/.exec(path);
    if (m) setAdsetIndex(Number(m[1]));
    if (path.startsWith("campaign")) setStep("campanha");
    else if (/^adsets\.\d+\.ads/.test(path)) setStep("anuncio");
    else setStep("conjunto");
    setHighlight(path);
  };

  const accountName = accounts.data?.find((a) => a.metaId === account)?.name ?? account;
  const locked = status === "publicando";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to="/app/$workspaceId/assistente"
            params={{ workspaceId }}
            className="text-sm text-muted-foreground hover:underline"
          >
            ← Rascunhos
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {data.campaign.name || "Nova campanha"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {accountName} · {currency} ·{" "}
            {save.isPending
              ? "salvando…"
              : dirty
                ? "alterações não salvas"
                : `salvo ${formatRelative(savedAt)} (v${version})`}
            {status === "publicado"
              ? " · publicado"
              : status === "erro"
                ? " · última publicação falhou"
                : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => save.mutate({ step, data })}
            loading={save.isPending}
            disabled={locked}
          >
            <Save />
            Salvar rascunho
          </Button>
          <Button
            variant="outline"
            onClick={() => saveTemplate.mutate()}
            loading={saveTemplate.isPending}
          >
            <Copy />
            Salvar como template
          </Button>
          <Button
            variant="ghost"
            className="text-destructive"
            onClick={() => remove.mutate()}
            loading={remove.isPending}
            disabled={locked}
          >
            <Trash2 />
            Excluir
          </Button>
        </div>
      </div>

      <ol className="flex flex-wrap gap-2" aria-label="Etapas">
        {WIZARD_STEPS.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              onClick={() => goTo(s)}
              aria-current={s === step ? "step" : undefined}
              className={cn(
                "rounded-full border px-3 py-1 text-sm",
                s === step
                  ? "border-primary bg-primary text-primary-foreground"
                  : i < stepIndex
                    ? "bg-accent"
                    : "hover:bg-accent",
              )}
            >
              {i + 1}. {WIZARD_STEP_LABELS[s]}
            </button>
          </li>
        ))}
      </ol>

      {step === "campanha" ? (
        <Card>
          <CardHeader>
            <CardTitle>Campanha</CardTitle>
            <CardDescription>
              Objetivo ODAX, categoria especial, orçamento (campanha ou por conjunto) e estratégia
              de lance.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field
              id="c-nome"
              label="Nome da campanha"
              error={issueAt("campaign.name")}
              highlight={highlight === "campaign.name"}
              className="sm:col-span-2"
            >
              <Input
                id="c-nome"
                value={data.campaign.name}
                onChange={(e) => update({ campaign: { ...data.campaign, name: e.target.value } })}
              />
            </Field>
            <Field id="c-obj" label="Objetivo" error={issueAt("campaign.objective")}>
              <Select
                value={data.campaign.objective}
                onValueChange={(v) => {
                  const objective = v as WizardDraft["campaign"]["objective"];
                  const first = OBJECTIVE_DESTINATIONS[objective][0];
                  update((d) => ({
                    ...d,
                    campaign: { ...d.campaign, objective },
                    adsets: d.adsets.map((a) =>
                      destinationOption(objective, a.destinationType)
                        ? a
                        : {
                            ...a,
                            destinationType: first?.destination ?? "WEBSITE",
                            optimizationGoal: first?.goals[0] ?? "LINK_CLICKS",
                            billingEvent: defaultBillingEvent(first?.goals[0] ?? "LINK_CLICKS"),
                          },
                    ),
                  }));
                }}
              >
                <SelectTrigger id="c-obj">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_OBJECTIVES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {objectiveLabel(o)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="c-lance" label="Estratégia de lance">
              <Select
                value={data.campaign.bidStrategy}
                onValueChange={(v) =>
                  update({
                    campaign: {
                      ...data.campaign,
                      bidStrategy: v as WizardDraft["campaign"]["bidStrategy"],
                    },
                  })
                }
              >
                <SelectTrigger id="c-lance">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BID_STRATEGIES.map((b) => (
                    <SelectItem key={b} value={b}>
                      {BID_STRATEGY_LABELS[b] ?? b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="space-y-2 sm:col-span-2">
              <Label>Categoria especial de anúncio</Label>
              <div className="flex flex-wrap gap-1.5">
                {SPECIAL_AD_CATEGORIES.filter((c) => c !== "NONE").map((c) => {
                  const on = data.campaign.specialAdCategories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={on}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs",
                        on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "hover:bg-accent",
                      )}
                      onClick={() =>
                        update({
                          campaign: {
                            ...data.campaign,
                            specialAdCategories: on
                              ? data.campaign.specialAdCategories.filter((x) => x !== c)
                              : [...data.campaign.specialAdCategories, c],
                          },
                        })
                      }
                    >
                      {SPECIAL_AD_CATEGORY_LABELS[c] ?? c}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Crédito, emprego, moradia, política, produtos financeiros e jogos de azar têm
                restrições de segmentação impostas pela Meta.
              </p>
            </div>
            <Field id="c-modo" label="Orçamento">
              <Select
                value={data.campaign.budgetMode}
                onValueChange={(v) =>
                  update({ campaign: { ...data.campaign, budgetMode: v as "CBO" | "ABO" } })
                }
              >
                <SelectTrigger id="c-modo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CBO">Da campanha (CBO / Advantage+)</SelectItem>
                  <SelectItem value="ABO">Por conjunto de anúncios (ABO)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {data.campaign.budgetMode === "CBO" ? (
              <div className="grid grid-cols-[auto_1fr] gap-2">
                <Field id="c-tipo" label="Tipo">
                  <Select
                    value={data.campaign.budgetKind}
                    onValueChange={(v) =>
                      update({
                        campaign: { ...data.campaign, budgetKind: v as "diario" | "vitalicio" },
                      })
                    }
                  >
                    <SelectTrigger id="c-tipo" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diario">Diário</SelectItem>
                      <SelectItem value="vitalicio">Vitalício</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  id="c-valor"
                  label={`Valor (${currency})`}
                  error={issueAt("campaign.budgetCents") ?? issueAt("campaign.budgetKind")}
                  highlight={highlight === "campaign.budgetCents"}
                >
                  <Input
                    id="c-valor"
                    inputMode="decimal"
                    defaultValue={centsToInput(data.campaign.budgetCents)}
                    onBlur={(e) =>
                      update({
                        campaign: { ...data.campaign, budgetCents: inputToCents(e.target.value) },
                      })
                    }
                    placeholder="ex.: 50,00"
                  />
                </Field>
              </div>
            ) : (
              <p className="self-end text-sm text-muted-foreground">
                Cada conjunto define o próprio orçamento na próxima etapa.
              </p>
            )}
            <p className="text-xs text-muted-foreground sm:col-span-2">
              Teste A/B: a criação como <code>ad_study</code> fica oculta nesta fase (ver README);
              publique duas campanhas e compare nos relatórios.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {step === "conjunto" ? (
        <AdSetStep
          workspaceId={workspaceId}
          account={account}
          currency={currency}
          data={data}
          update={update}
          index={adsetIndex}
          setIndex={setAdsetIndex}
          issueAt={issueAt}
          highlight={highlight}
          options={options.data}
        />
      ) : null}

      {step === "anuncio" ? (
        <AdStep
          workspaceId={workspaceId}
          account={account}
          data={data}
          update={update}
          index={adsetIndex}
          setIndex={setAdsetIndex}
          issueAt={issueAt}
          highlight={highlight}
          options={options.data}
        />
      ) : null}

      {step === "revisao" ? (
        <Card>
          <CardHeader>
            <CardTitle>Revisão e publicação</CardTitle>
            <CardDescription>
              Tudo é criado na Meta em sequência (mídias → campanha → conjuntos → criativos →
              anúncios), pausado por padrão. Se um passo falhar, o que foi criado é apagado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Campanha</dt>
                <dd className="font-medium">{data.campaign.name || "—"}</dd>
                <dd className="text-xs text-muted-foreground">
                  {objectiveLabel(data.campaign.objective)} ·{" "}
                  {data.campaign.budgetMode === "CBO"
                    ? `${formatMoney(data.campaign.budgetCents, currency)} ${data.campaign.budgetKind === "diario" ? "/dia" : "vitalício"}`
                    : "orçamento por conjunto"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Conjuntos</dt>
                <dd className="font-medium">{data.adsets.length}</dd>
                <dd className="text-xs text-muted-foreground">
                  {data.adsets.map((a) => a.name).join(", ") || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Anúncios</dt>
                <dd className="font-medium">{data.adsets.reduce((n, a) => n + a.ads.length, 0)}</dd>
                <dd className="text-xs text-muted-foreground">
                  {data.adsets
                    .flatMap((a) => a.ads.map((ad) => AD_FORMAT_LABELS[ad.format]))
                    .join(", ") || "—"}
                </dd>
              </div>
            </dl>
            {issues.length > 0 ? (
              <Alert variant="destructive">
                <AlertTitle>{issues.length} pendência(s) antes de publicar</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {issues.map((i) => (
                      <li key={i.path}>
                        <button
                          type="button"
                          className="underline"
                          onClick={() => jumpToIssue(i.path)}
                        >
                          {i.message}
                        </button>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertTitle>Pronto para publicar</AlertTitle>
                <AlertDescription>
                  Nenhuma pendência encontrada nas regras locais; a Meta ainda valida tudo ao criar.
                </AlertDescription>
              </Alert>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={activate}
                onChange={(e) => setActivate(e.target.checked)}
              />
              Ativar campanha, conjuntos e anúncios ao final (padrão: tudo pausado)
            </label>
            <Button
              onClick={() => publish.mutate()}
              loading={publish.isPending || locked}
              disabled={issues.length > 0 || locked}
            >
              <Rocket />
              Publicar na Meta
            </Button>
            {publicationId ? (
              <PublicationProgress
                workspaceId={workspaceId}
                publicationId={publicationId}
                onFieldError={jumpToIssue}
                onFinished={(p) => {
                  setStatus(p.status === "concluida" ? "publicado" : "erro");
                  void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
                  void queryClient.invalidateQueries({
                    queryKey: ["workspaces", workspaceId, "campaigns"],
                  });
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="flex justify-between">
        <Button
          variant="outline"
          disabled={stepIndex === 0}
          onClick={() => goTo(WIZARD_STEPS[stepIndex - 1] ?? "campanha")}
        >
          <ArrowLeft />
          Anterior
        </Button>
        {stepIndex < WIZARD_STEPS.length - 1 ? (
          <Button onClick={() => goTo(WIZARD_STEPS[stepIndex + 1] ?? "revisao")}>
            Próxima etapa
            <ArrowRight />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  error,
  highlight,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  highlight?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "space-y-1.5",
        highlight && "rounded-md ring-2 ring-amber-500 ring-offset-2",
        className,
      )}
    >
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

type Options = CreationOptionsDto | undefined;

function AdSetStep({
  workspaceId,
  account,
  currency,
  data,
  update,
  index,
  setIndex,
  issueAt,
  highlight,
  options,
}: {
  workspaceId: string;
  account: string;
  currency: string;
  data: WizardDraft;
  update: (p: (d: WizardDraft) => WizardDraft) => void;
  index: number;
  setIndex: (i: number) => void;
  issueAt: (p: string) => string | undefined;
  highlight: string | null;
  options: Options;
}) {
  const adset = data.adsets[index];
  const setAdset = (patch: Partial<AdSetDraft>) =>
    update((d) => ({
      ...d,
      adsets: d.adsets.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    }));
  const destinations = OBJECTIVE_DESTINATIONS[data.campaign.objective];
  const option = adset
    ? destinationOption(data.campaign.objective, adset.destinationType)
    : undefined;
  const p = `adsets.${index}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Conjuntos de anúncios</CardTitle>
        <CardDescription>
          Local de conversão compatível com o objetivo, otimização, pixel/página, orçamento (se
          ABO), agenda, público e posicionamentos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {data.adsets.map((a, i) => (
            <button
              key={a.key}
              type="button"
              onClick={() => setIndex(i)}
              className={cn(
                "rounded-md border px-3 py-1 text-sm",
                i === index
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:bg-accent",
              )}
            >
              {a.name || `Conjunto ${i + 1}`}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              update((d) => ({ ...d, adsets: [...d.adsets, newAdSet(d.adsets.length + 1)] }));
              setIndex(data.adsets.length);
            }}
          >
            <Plus />
            Adicionar conjunto
          </Button>
          {adset ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                update((d) => ({ ...d, adsets: d.adsets.filter((_, i) => i !== index) }));
                setIndex(Math.max(0, index - 1));
              }}
            >
              <Trash2 />
              Remover
            </Button>
          ) : null}
        </div>
        {issueAt("adsets") ? <p className="text-sm text-destructive">{issueAt("adsets")}</p> : null}
        {adset ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="s-nome"
              label="Nome do conjunto"
              error={issueAt(`${p}.name`)}
              highlight={highlight === `${p}.name`}
              className="sm:col-span-2"
            >
              <Input
                id="s-nome"
                value={adset.name}
                onChange={(e) => setAdset({ name: e.target.value })}
              />
            </Field>
            <Field id="s-dest" label="Local de conversão" error={issueAt(`${p}.destinationType`)}>
              <Select
                value={adset.destinationType}
                onValueChange={(v) => {
                  const opt = destinationOption(data.campaign.objective, v);
                  const goal = opt?.goals[0] ?? adset.optimizationGoal;
                  setAdset({
                    destinationType: v as AdSetDraft["destinationType"],
                    optimizationGoal: goal,
                    billingEvent: defaultBillingEvent(goal),
                  });
                }}
              >
                <SelectTrigger id="s-dest">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((d) => (
                    <SelectItem key={d.destination} value={d.destination}>
                      {DESTINATION_TYPE_LABELS[d.destination]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              id="s-goal"
              label="Evento de otimização"
              error={issueAt(`${p}.optimizationGoal`)}
            >
              <Select
                value={adset.optimizationGoal}
                onValueChange={(v) =>
                  setAdset({
                    optimizationGoal: v as AdSetDraft["optimizationGoal"],
                    billingEvent: defaultBillingEvent(v as AdSetDraft["optimizationGoal"]),
                  })
                }
              >
                <SelectTrigger id="s-goal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(option?.goals ?? [adset.optimizationGoal]).map((g) => (
                    <SelectItem key={g} value={g}>
                      {optimizationGoalLabel(g)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="s-bill" label="Cobrança">
              <Select
                value={adset.billingEvent}
                onValueChange={(v) => setAdset({ billingEvent: v as AdSetDraft["billingEvent"] })}
              >
                <SelectTrigger id="s-bill">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_EVENTS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {optimizationGoalLabel(b)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {option?.needsPixel ? (
              <>
                <Field
                  id="s-pixel"
                  label="Pixel"
                  error={issueAt(`${p}.pixelId`)}
                  highlight={highlight === `${p}.pixelId`}
                >
                  <Select
                    value={adset.pixelId ?? NONE}
                    onValueChange={(v) => setAdset({ pixelId: v === NONE ? null : v })}
                  >
                    <SelectTrigger id="s-pixel">
                      <SelectValue placeholder="Escolha o pixel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Escolha…</SelectItem>
                      {(options?.pixels ?? []).map((px) => (
                        <SelectItem key={px.id} value={px.id}>
                          {px.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  id="s-evento"
                  label="Evento de conversão"
                  error={issueAt(`${p}.customEventType`)}
                >
                  <Select
                    value={adset.customEventType ?? NONE}
                    onValueChange={(v) =>
                      setAdset({
                        customEventType: v === NONE ? null : (v as AdSetDraft["customEventType"]),
                      })
                    }
                  >
                    <SelectTrigger id="s-evento">
                      <SelectValue placeholder="Escolha o evento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Escolha…</SelectItem>
                      {PIXEL_EVENTS.map((ev) => (
                        <SelectItem key={ev} value={ev}>
                          {PIXEL_EVENT_LABELS[ev]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </>
            ) : null}
            {option?.needsPage ? (
              <Field
                id="s-page"
                label="Página"
                error={issueAt(`${p}.pageId`)}
                highlight={highlight === `${p}.pageId`}
              >
                <Select
                  value={adset.pageId ?? NONE}
                  onValueChange={(v) => setAdset({ pageId: v === NONE ? null : v })}
                >
                  <SelectTrigger id="s-page">
                    <SelectValue placeholder="Escolha a página" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Escolha…</SelectItem>
                    {(options?.pages ?? []).map((pg) => (
                      <SelectItem key={pg.id} value={pg.id}>
                        {pg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : null}
            {option?.needsApp ? (
              <>
                <Field
                  id="s-app"
                  label="Id do app (promoted_object)"
                  error={issueAt(`${p}.applicationId`)}
                >
                  <Input
                    id="s-app"
                    value={adset.applicationId ?? ""}
                    onChange={(e) => setAdset({ applicationId: e.target.value || null })}
                  />
                </Field>
                <Field id="s-store" label="URL da loja (object_store_url)">
                  <Input
                    id="s-store"
                    value={adset.objectStoreUrl ?? ""}
                    onChange={(e) => setAdset({ objectStoreUrl: e.target.value || null })}
                  />
                </Field>
              </>
            ) : null}
            {data.campaign.budgetMode === "ABO" ? (
              <div className="grid grid-cols-[auto_1fr] gap-2">
                <Field id="s-tipo" label="Orçamento">
                  <Select
                    value={adset.budgetKind}
                    onValueChange={(v) => setAdset({ budgetKind: v as "diario" | "vitalicio" })}
                  >
                    <SelectTrigger id="s-tipo" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diario">Diário</SelectItem>
                      <SelectItem value="vitalicio">Vitalício</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  id="s-valor"
                  label={`Valor (${currency})`}
                  error={issueAt(`${p}.budgetCents`)}
                  highlight={highlight === `${p}.budgetCents`}
                >
                  <Input
                    id="s-valor"
                    inputMode="decimal"
                    defaultValue={centsToInput(adset.budgetCents)}
                    onBlur={(e) => setAdset({ budgetCents: inputToCents(e.target.value) })}
                    placeholder="ex.: 30,00"
                  />
                </Field>
              </div>
            ) : null}
            {data.campaign.bidStrategy === "LOWEST_COST_WITH_BID_CAP" ||
            data.campaign.bidStrategy === "COST_CAP" ? (
              <Field
                id="s-bid"
                label={`Valor do lance (${currency})`}
                error={issueAt(`${p}.bidAmountCents`)}
              >
                <Input
                  id="s-bid"
                  inputMode="decimal"
                  defaultValue={centsToInput(adset.bidAmountCents ?? 0)}
                  onBlur={(e) => setAdset({ bidAmountCents: inputToCents(e.target.value) || null })}
                />
              </Field>
            ) : null}
            <Field id="s-inicio" label="Início (opcional)">
              <Input
                id="s-inicio"
                type="datetime-local"
                value={adset.startTime ? adset.startTime.slice(0, 16) : ""}
                onChange={(e) =>
                  setAdset({
                    startTime: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
              />
            </Field>
            <Field
              id="s-fim"
              label="Término (obrigatório com orçamento vitalício)"
              error={issueAt(`${p}.endTime`)}
              highlight={highlight === `${p}.endTime`}
            >
              <Input
                id="s-fim"
                type="datetime-local"
                value={adset.endTime ? adset.endTime.slice(0, 16) : ""}
                onChange={(e) =>
                  setAdset({
                    endTime: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
              />
            </Field>
            <div
              className={cn(
                "space-y-2 sm:col-span-2",
                highlight?.startsWith(`${p}.targeting`) &&
                  "rounded-md ring-2 ring-amber-500 ring-offset-2",
              )}
            >
              <Label>Público</Label>
              {issueAt(`${p}.targeting.countries`) ? (
                <p className="text-xs text-destructive">{issueAt(`${p}.targeting.countries`)}</p>
              ) : null}
              {(options?.savedAudiences.length ?? 0) > 0 ? (
                <select
                  className="h-9 w-full rounded-md border bg-transparent px-2 text-sm sm:w-80"
                  value=""
                  aria-label="Usar público salvo"
                  onChange={(e) => {
                    const saved = options?.savedAudiences.find((s) => s.id === e.target.value);
                    if (!saved) return;
                    const { targeting, placements } = targetingFromMeta(saved.targeting);
                    setAdset({
                      targeting,
                      placements: placements.automatic ? adset.placements : placements,
                    });
                  }}
                >
                  <option value="">Começar de um público salvo…</option>
                  {options?.savedAudiences.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <TargetingEditor
                workspaceId={workspaceId}
                account={account}
                value={adset.targeting}
                onChange={(t) => setAdset({ targeting: t })}
                optimizationGoal={adset.optimizationGoal}
                placements={adset.placements}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Posicionamentos</Label>
              {issueAt(`${p}.placements.publisherPlatforms`) ? (
                <p className="text-xs text-destructive">
                  {issueAt(`${p}.placements.publisherPlatforms`)}
                </p>
              ) : null}
              <PlacementsEditor
                value={adset.placements}
                onChange={(pl) => setAdset({ placements: pl })}
              />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AdStep({
  workspaceId,
  account,
  data,
  update,
  index,
  setIndex,
  issueAt,
  highlight,
  options,
}: {
  workspaceId: string;
  account: string;
  data: WizardDraft;
  update: (p: (d: WizardDraft) => WizardDraft) => void;
  index: number;
  setIndex: (i: number) => void;
  issueAt: (p: string) => string | undefined;
  highlight: string | null;
  options: Options;
}) {
  const adset = data.adsets[index];
  const [adIndex, setAdIndex] = useState(0);
  const ad = adset?.ads[adIndex];
  const [picker, setPicker] = useState<{
    kind: "imagem" | "video" | undefined;
    onPick: (a: CreativeAssetDto) => void;
  } | null>(null);
  const [preview, setPreview] = useState<{
    format: string;
    data: PreviewDto | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  // Fase 8: variações de texto geradas pela IA (a pessoa escolhe e aplica).
  const [aiOpen, setAiOpen] = useState(false);
  const setAd = (patch: Partial<AdDraft>) =>
    update((d) => ({
      ...d,
      adsets: d.adsets.map((a, i) =>
        i === index
          ? { ...a, ads: a.ads.map((x, j) => (j === adIndex ? { ...x, ...patch } : x)) }
          : a,
      ),
    }));
  const p = `adsets.${index}.ads.${adIndex}`;
  const pages = options?.pages ?? [];
  const page = pages.find((pg) => pg.id === ad?.pageId);
  const needsLink = adset?.destinationType === "WEBSITE" || adset?.destinationType === "APP";

  const runPreview = (format: string) => {
    if (!ad || !adset) return;
    setPreview({ format, data: null, loading: true, error: null });
    void creationApi
      .preview(workspaceId, {
        adAccountMetaId: account,
        ad,
        format,
        campaignName: data.campaign.name,
        adsetName: adset.name,
      })
      .then((d) => setPreview({ format, data: d, loading: false, error: null }))
      .catch((err: unknown) =>
        setPreview({ format, data: null, loading: false, error: errorMessage(err) }),
      );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Anúncios</CardTitle>
        <CardDescription>
          Identidade (página + Instagram), formato, textos, botão, URL com UTMs e criativo da
          biblioteca. A prévia é a real, gerada pela Meta por posicionamento.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Conjunto:</span>
          {data.adsets.map((a, i) => (
            <button
              key={a.key}
              type="button"
              onClick={() => {
                setIndex(i);
                setAdIndex(0);
              }}
              className={cn(
                "rounded-md border px-3 py-1 text-sm",
                i === index
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:bg-accent",
              )}
            >
              {a.name || `Conjunto ${i + 1}`}
            </button>
          ))}
        </div>
        {adset ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Anúncio:</span>
            {adset.ads.map((a, j) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setAdIndex(j)}
                className={cn(
                  "rounded-md border px-3 py-1 text-sm",
                  j === adIndex
                    ? "border-primary bg-primary text-primary-foreground"
                    : "hover:bg-accent",
                )}
              >
                {a.name || `Anúncio ${j + 1}`}
              </button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                update((d) => ({
                  ...d,
                  adsets: d.adsets.map((a, i) =>
                    i === index
                      ? { ...a, ads: [...a.ads, newAd(a.ads.length + 1, pages[0]?.id ?? null)] }
                      : a,
                  ),
                }));
                setAdIndex(adset.ads.length);
              }}
            >
              <Plus />
              Adicionar anúncio
            </Button>
            {ad ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => {
                  update((d) => ({
                    ...d,
                    adsets: d.adsets.map((a, i) =>
                      i === index ? { ...a, ads: a.ads.filter((_, j) => j !== adIndex) } : a,
                    ),
                  }));
                  setAdIndex(Math.max(0, adIndex - 1));
                }}
              >
                <Trash2 />
                Remover
              </Button>
            ) : null}
            {issueAt(`adsets.${index}.ads`) ? (
              <p className="text-sm text-destructive">{issueAt(`adsets.${index}.ads`)}</p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Crie um conjunto na etapa anterior.</p>
        )}
        {ad && adset ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="a-nome"
                label="Nome do anúncio"
                error={issueAt(`${p}.name`)}
                highlight={highlight === `${p}.name`}
                className="sm:col-span-2"
              >
                <Input
                  id="a-nome"
                  value={ad.name}
                  onChange={(e) => setAd({ name: e.target.value })}
                />
              </Field>
              <Field
                id="a-page"
                label="Página"
                error={issueAt(`${p}.pageId`)}
                highlight={highlight === `${p}.pageId`}
              >
                <Select
                  value={ad.pageId ?? NONE}
                  onValueChange={(v) =>
                    setAd({ pageId: v === NONE ? null : v, instagramUserId: null })
                  }
                >
                  <SelectTrigger id="a-page">
                    <SelectValue placeholder="Escolha a página" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Escolha…</SelectItem>
                    {pages.map((pg) => (
                      <SelectItem key={pg.id} value={pg.id}>
                        {pg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="a-ig" label="Instagram">
                <Select
                  value={ad.instagramUserId ?? NONE}
                  onValueChange={(v) => setAd({ instagramUserId: v === NONE ? null : v })}
                >
                  <SelectTrigger id="a-ig">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Usar a página (sem conta própria)</SelectItem>
                    {page?.instagramUserId ? (
                      <SelectItem value={page.instagramUserId}>
                        @{page.instagramUsername ?? page.instagramUserId}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="a-fmt" label="Formato">
                <Select
                  value={ad.format}
                  onValueChange={(v) =>
                    setAd({
                      format: v as AdDraft["format"],
                      cards:
                        v === "carrossel" && ad.cards.length === 0
                          ? [
                              { assetId: null, headline: "", description: "", link: "" },
                              { assetId: null, headline: "", description: "", link: "" },
                            ]
                          : ad.cards,
                    })
                  }
                >
                  <SelectTrigger id="a-fmt">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_FORMATS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {AD_FORMAT_LABELS[f]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="a-cta" label="Botão (CTA)">
                <Select value={ad.cta} onValueChange={(v) => setAd({ cta: v as AdDraft["cta"] })}>
                  <SelectTrigger id="a-cta">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_TO_ACTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {CALL_TO_ACTION_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                id="a-texto"
                label="Texto principal"
                error={issueAt(`${p}.primaryText`)}
                highlight={highlight === `${p}.primaryText`}
                className="sm:col-span-2"
              >
                <Textarea
                  id="a-texto"
                  value={ad.primaryText}
                  onChange={(e) => setAd({ primaryText: e.target.value })}
                />
                <div className="mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setAiOpen(true)}>
                    <Sparkles />
                    Gerar variações com IA
                  </Button>
                </div>
                <VariationsDialog
                  open={aiOpen}
                  onOpenChange={setAiOpen}
                  workspaceId={workspaceId}
                  objective={data.campaign.objective}
                  initialProduct={data.campaign.name}
                  onApply={(v) =>
                    setAd({
                      primaryText: v.primaryText,
                      headline: v.headline,
                      description: v.description,
                      cta: v.cta,
                    })
                  }
                />
              </Field>
              <Field id="a-titulo" label="Título">
                <Input
                  id="a-titulo"
                  value={ad.headline}
                  onChange={(e) => setAd({ headline: e.target.value })}
                  maxLength={255}
                />
              </Field>
              <Field id="a-desc" label="Descrição">
                <Input
                  id="a-desc"
                  value={ad.description}
                  onChange={(e) => setAd({ description: e.target.value })}
                  maxLength={255}
                />
              </Field>
              <Field
                id="a-link"
                label={needsLink ? "URL de destino" : "URL (opcional)"}
                error={issueAt(`${p}.link`)}
                highlight={highlight === `${p}.link`}
                className="sm:col-span-2"
              >
                <Input
                  id="a-link"
                  value={ad.link}
                  onChange={(e) => setAd({ link: e.target.value })}
                  placeholder="https://"
                />
                {ad.link ? (
                  <p className="truncate text-xs text-muted-foreground">
                    Final:{" "}
                    {linkWithUtm(ad.link, ad.utm, ad.utmEnabled, {
                      campaignName: data.campaign.name,
                      adsetName: adset.name,
                      adName: ad.name,
                    })}
                  </p>
                ) : null}
              </Field>
              <div className="space-y-2 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={ad.utmEnabled}
                    onChange={(e) => setAd({ utmEnabled: e.target.checked })}
                  />
                  UTMs automáticos (editáveis; aceitam {"{{campaign.name}}"}, {"{{adset.name}}"} e{" "}
                  {"{{ad.name}}"})
                </label>
                {ad.utmEnabled ? (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {(["source", "medium", "campaign", "content", "term"] as const).map((k) => (
                      <Input
                        key={k}
                        aria-label={`utm_${k}`}
                        value={ad.utm[k]}
                        onChange={(e) => setAd({ utm: { ...ad.utm, [k]: e.target.value } })}
                        placeholder={`utm_${k}`}
                        className="h-8 text-xs"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              {ad.format === "imagem" ? (
                <MediaSlot
                  label="Imagem"
                  error={issueAt(`${p}.imageAssetId`)}
                  highlight={highlight === `${p}.imageAssetId`}
                  assetId={ad.imageAssetId}
                  workspaceId={workspaceId}
                  onPick={() =>
                    setPicker({ kind: "imagem", onPick: (a) => setAd({ imageAssetId: a.id }) })
                  }
                  onClear={() => setAd({ imageAssetId: null })}
                />
              ) : null}
              {ad.format === "video" ? (
                <>
                  <MediaSlot
                    label="Vídeo"
                    error={issueAt(`${p}.videoAssetId`)}
                    highlight={highlight === `${p}.videoAssetId`}
                    assetId={ad.videoAssetId}
                    workspaceId={workspaceId}
                    onPick={() =>
                      setPicker({ kind: "video", onPick: (a) => setAd({ videoAssetId: a.id }) })
                    }
                    onClear={() => setAd({ videoAssetId: null })}
                  />
                  <MediaSlot
                    label="Capa (opcional)"
                    assetId={ad.thumbnailAssetId}
                    workspaceId={workspaceId}
                    onPick={() =>
                      setPicker({
                        kind: "imagem",
                        onPick: (a) => setAd({ thumbnailAssetId: a.id }),
                      })
                    }
                    onClear={() => setAd({ thumbnailAssetId: null })}
                  />
                </>
              ) : null}
              {ad.format === "carrossel" ? (
                <div className="space-y-2 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>
                      Cartões ({ad.cards.length}/{CAROUSEL_MAX_CARDS})
                    </Label>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={ad.cards.length >= CAROUSEL_MAX_CARDS}
                      onClick={() =>
                        setAd({
                          cards: [
                            ...ad.cards,
                            { assetId: null, headline: "", description: "", link: "" },
                          ],
                        })
                      }
                    >
                      <Plus />
                      Cartão
                    </Button>
                  </div>
                  {issueAt(`${p}.cards`) ? (
                    <p className="text-xs text-destructive">{issueAt(`${p}.cards`)}</p>
                  ) : null}
                  <div className="grid gap-2 sm:grid-cols-2">
                    {ad.cards.map((card, ci) => (
                      <div
                        key={ci}
                        className={cn(
                          "space-y-2 rounded-md border p-2",
                          highlight?.startsWith(`${p}.cards.${ci}`) && "ring-2 ring-amber-500",
                        )}
                      >
                        <MediaSlot
                          label={`Cartão ${ci + 1}`}
                          error={issueAt(`${p}.cards.${ci}.assetId`)}
                          assetId={card.assetId}
                          workspaceId={workspaceId}
                          onPick={() =>
                            setPicker({
                              kind: undefined,
                              onPick: (a) =>
                                setAd({
                                  cards: ad.cards.map((c, k) =>
                                    k === ci ? { ...c, assetId: a.id } : c,
                                  ),
                                }),
                            })
                          }
                          onClear={() =>
                            setAd({
                              cards: ad.cards.map((c, k) =>
                                k === ci ? { ...c, assetId: null } : c,
                              ),
                            })
                          }
                        />
                        <Input
                          aria-label="Título do cartão"
                          placeholder="Título"
                          value={card.headline}
                          onChange={(e) =>
                            setAd({
                              cards: ad.cards.map((c, k) =>
                                k === ci ? { ...c, headline: e.target.value } : c,
                              ),
                            })
                          }
                          className="h-8"
                        />
                        <Input
                          aria-label="Link do cartão"
                          placeholder="Link (opcional)"
                          value={card.link}
                          onChange={(e) =>
                            setAd({
                              cards: ad.cards.map((c, k) =>
                                k === ci ? { ...c, link: e.target.value } : c,
                              ),
                            })
                          }
                          className="h-8"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => setAd({ cards: ad.cards.filter((_, k) => k !== ci) })}
                        >
                          Remover cartão
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label>Prévia real (Meta)</Label>
              <Select value={preview?.format ?? "MOBILE_FEED_STANDARD"} onValueChange={runPreview}>
                <SelectTrigger aria-label="Posicionamento da prévia">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PREVIEW_FORMATS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => runPreview(preview?.format ?? "MOBILE_FEED_STANDARD")}
                loading={preview?.loading ?? false}
              >
                <Eye />
                Gerar prévia
              </Button>
              {preview?.error ? <p className="text-xs text-destructive">{preview.error}</p> : null}
              {preview?.data ? (
                <div
                  className="overflow-auto rounded-md border bg-white p-1"
                  dangerouslySetInnerHTML={{ __html: preview.data.body }}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  A prévia usa o endpoint generatepreviews da Meta e exige que as mídias já estejam
                  na conta (o botão envia se precisar).
                </p>
              )}
            </div>
          </div>
        ) : null}
        {picker ? (
          <AssetPicker
            workspaceId={workspaceId}
            open
            onOpenChange={(o) => (o ? undefined : setPicker(null))}
            {...(picker.kind ? { kind: picker.kind } : {})}
            onPick={(a) => {
              picker.onPick(a);
              // Envia para a conta desde já, para a prévia e a publicação não esperarem.
              void creationApi.pushAsset(workspaceId, a.id, account).catch(() => undefined);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function MediaSlot({
  label,
  assetId,
  workspaceId,
  onPick,
  onClear,
  error,
  highlight,
}: {
  label: string;
  assetId: string | null;
  workspaceId: string;
  onPick: () => void;
  onClear: () => void;
  error?: string | undefined;
  highlight?: boolean;
}) {
  const asset = useQuery({
    ...queries.creative(workspaceId, assetId ?? ""),
    enabled: assetId !== null,
  });
  return (
    <div
      className={cn("space-y-1.5", highlight && "rounded-md ring-2 ring-amber-500 ring-offset-2")}
    >
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        {asset.data ? (
          <AssetThumb asset={asset.data} className="size-16 rounded-md" />
        ) : (
          <div className="flex size-16 items-center justify-center rounded-md border border-dashed text-muted-foreground">
            <ImageIcon className="size-5" aria-hidden />
          </div>
        )}
        <div className="min-w-0 space-y-1">
          {asset.data ? (
            <p className="truncate text-sm">{asset.data.name}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum criativo</p>
          )}
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="outline" onClick={onPick}>
              Escolher da biblioteca
            </Button>
            {assetId ? (
              <Button type="button" size="sm" variant="ghost" onClick={onClear}>
                Limpar
              </Button>
            ) : null}
          </div>
          {asset.data?.warnings.length ? (
            <Badge variant="warning">{asset.data.warnings.length} aviso(s) de posicionamento</Badge>
          ) : null}
        </div>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
